import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Switch,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  useTheme as useMuiTheme,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useTheme } from "../contexts/ThemeContext";
import {
  DATA_SOURCES_PANEL_Z_INDEX,
  DATA_SOURCES_BACKDROP_Z_INDEX,
  DATA_SOURCES_DIALOG_Z_INDEX,
} from "../helpers/zIndexLayers";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import { LanguageConstants } from "../helpers/LanguageConstants";
import { useChannelAdmin } from "../hooks/useChannelAdmin";
import {
  getDataSourceIcon,
  getDownloadSourceParams,
  isDatasourceSupportedForPreview,
  type DownloadSourceParams,
} from "../helpers/dataSourceUtils";
import {
  API_CONFIG,
  API_ENDPOINTS,
  STORAGE_KEYS,
  SOCKET_KEYS,
} from "../helpers/constants";
import socketService from "../services/socketService";
import AddLogDataSourceModal from "./AddLogDataSourceModal";
import { fetchSkills, getSkillLinkedDatasourceIds } from "../services/skillsService";
import DatasourceViewModal, {
  downloadDatasource,
} from "./DatasourceViewModal";

// Interfaces
export interface DataSource {
  id: string;
  name: string;
  type: string;
  storage_type?: string;
  provider?: string | null;
  app_type?: string;
  database_type?: string | null;
  file_type?: string;
  url?: string;
  filename?: string;
  status: "active" | "inactive";
  is_connected: boolean;
  embedding_status?: number;
  is_embedding_required?: boolean;
  created_at: string;
  updated_at: string;
  // Website datasources (webscrapping_UI branch) — present only when storage_type === 'website'
  crawl_depth?: number;
  max_pages?: number;
  scraped_pages?: number | null;
}

export interface DataSourcesResponse {
  datasources: DataSource[];
  total: number;
  page: number;
  size: number;
  /** Total pages when API returns paginated metadata */
  pages?: number;
}

/** Page size for channel datasource list (must match API `size` query) */
const DATASOURCE_PAGE_SIZE = 10;

/** Whether another page of datasources exists after this response */
const computeDatasourcesHasMore = (data: DataSourcesResponse): boolean => {
  const size = Math.max(data.size, 1);
  const totalPages =
    data.pages ?? Math.max(1, Math.ceil(data.total / size));
  return data.page < totalPages;
};

export interface EmbeddingDataSource {
  id: string;
  name: string;
  filename: string;
  is_embedding_required: boolean;
  embedding_status: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  // Website datasources (webscrapping_UI branch) — present only when storage_type === 'website'
  url?: string;
  crawl_depth?: number;
  max_pages?: number;
  scraped_pages?: number | null;
}

export interface EmbeddingStatusResponse {
  channel_id: string;
  total_datasources: number;
  embedding_required: number;
  in_progress: number;
  completed: number;
  failed: number;
  not_required: number;
  datasources: EmbeddingDataSource[];
}

// Attachment interface
export interface ThreadAttachment {
  attachment_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  storage_path: string;
  is_inline: boolean;
  content_id: string | null;
}

// Props interface
export interface DataSourcesPanelProps {
  channelId: string | null;
  channelName?: string;
  isOpen: boolean;
  onClose: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  threadId?: string | null;
  messages?: Array<{
    attachments: ThreadAttachment[] | null;
  }>;
  /** Opens the Add Data Source dialog once after a deep link opens this panel. */
  initialOpenAddDataSource?: boolean;
  /** Called after the deep-link dialog request has been consumed. */
  onInitialAddDataSourceOpened?: () => void;
  /** Called when a data source is successfully added so parent can refresh (e.g. embedding status for Ask Prism7 button) */
  onDataSourceAdded?: () => void;
}

// Helper functions
const getDataSourceDisplayName = (datasource: DataSource) => {
  // The list endpoint never actually sends a `type` field (confirmed by tracing all
  // consumers of GET /datasources/) — only `storage_type`, plus `url` on website entries.
  // storage_type === 'website' (falling back to url presence for older/looser payloads)
  // is the real discriminator; `type === "url"` here always evaluated false for website
  // rows, leaving their name blank (webscrapping_UI fix).
  const isWebsite = datasource.storage_type === 'website' || Boolean(datasource.url);
  if (isWebsite && datasource.url) {
    return datasource.url.length > 30
      ? `${datasource.url.substring(0, 30)}...`
      : datasource.url;
  }
  if (datasource.type === "file" && datasource.filename) {
    return datasource.filename;
  }
  return datasource.filename || datasource.name || "";
};

const getMessageAttachmentDisplayName = (
  attachmentId: string,
  messages: Array<{ attachments: ThreadAttachment[] | null }>
): string => {
  for (const message of messages) {
    if (message.attachments) {
      const att = message.attachments.find(
        (a) => !a.is_inline && a.attachment_id === attachmentId
      );
      if (att) return att.filename;
    }
  }
  return "";
};

// Normalize for comparison (trim, lowerCase, strip trailing "...") so LIST and embedding APIs match
const normalizeName = (s: string | undefined): string =>
  (s ?? "").trim().toLowerCase().replace(/\.\.\.$/, "");

// True if two display names refer to the same file (exact, or one truncated form of the other)
const sameDatasourceName = (
  a: string | undefined,
  b: string | undefined
): boolean => {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  return na.startsWith(nb) || nb.startsWith(na);
};

const getDatasourceEmbeddingStatus = (
  datasourceId: string,
  embeddingStatus: EmbeddingStatusResponse | null,
  datasource?: DataSource
): number | null => {
  // Optimistic "pending" rows (upload done, create-bulk in progress) show as in progress
  if (datasourceId.startsWith("pending-")) {
    return 1;
  }
  if (embeddingStatus?.datasources) {
    const byId = embeddingStatus.datasources.find((ds) => ds.id === datasourceId);
    if (byId) return byId.embedding_status;
    if (datasource) {
      const byFilename = embeddingStatus.datasources.find((ds) =>
        sameDatasourceName(ds.filename, datasource.filename) ||
        sameDatasourceName(ds.filename, datasource.name) ||
        sameDatasourceName(ds.name, datasource.filename) ||
        sameDatasourceName(ds.name, datasource.name)
      );
      if (byFilename) return byFilename.embedding_status;
    }
  }
  return null;
};

// Same matching logic as getDatasourceEmbeddingStatus, but returns the full matched entry
// (webscrapping_UI branch) so renderEmbeddingStatusChip can read error_message/scraped_pages
// without duplicating the id/filename lookup.
const getDatasourceEmbeddingEntry = (
  datasourceId: string,
  embeddingStatus: EmbeddingStatusResponse | null,
  datasource?: DataSource
): EmbeddingDataSource | null => {
  if (!embeddingStatus?.datasources) return null;
  const byId = embeddingStatus.datasources.find((ds) => ds.id === datasourceId);
  if (byId) return byId;
  if (datasource) {
    const byFilename = embeddingStatus.datasources.find((ds) =>
      sameDatasourceName(ds.filename, datasource.filename) ||
      sameDatasourceName(ds.filename, datasource.name) ||
      sameDatasourceName(ds.name, datasource.filename) ||
      sameDatasourceName(ds.name, datasource.name)
    );
    if (byFilename) return byFilename;
  }
  return null;
};

const getMessageAttachmentEmbeddingStatus = (
  attachmentId: string,
  filename: string,
  embeddingStatus: EmbeddingStatusResponse | null
): number | null => {
  if (!embeddingStatus?.datasources) return null;
  const match =
    embeddingStatus.datasources.find((ds) => ds.id === attachmentId) ??
    embeddingStatus.datasources.find(
      (ds) => ds.filename === filename || ds.name === filename
    );
  return match?.embedding_status ?? null;
};

const renderMessageAttachmentEmbeddingChip = (
  attachmentId: string,
  filename: string,
  embeddingStatus: EmbeddingStatusResponse | null,
  retryLoadingMap: Record<string, boolean>,
  onRetry: (datasourceId: string) => void
) => {
  const status = getMessageAttachmentEmbeddingStatus(
    attachmentId,
    filename,
    embeddingStatus
  );
  if (status === null) {
    return (
      <Chip
        label=""
        color="default"
        size="small"
        sx={{ fontSize: "0.75rem", height: "20px" }}
      />
    );
  }
  let label: string;
  let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  switch (status) {
    case 1:
      label = "In Progress";
      color = "warning";
      break;
    case 2:
      label = "Completed";
      color = "success";
      break;
    case 3:
      label = "Failure";
      color = "error";
      break;
    default:
      label = "Unknown";
      color = "default";
      break;
  }
  const isRetryLoading = retryLoadingMap[attachmentId] || false;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Chip
        label={label}
        color={color}
        size="small"
        sx={{ fontSize: "0.75rem", height: "20px", ml: 1 }}
      />
      {status === 3 && (
        <Tooltip title="Retry embedding">
          <span>
            <IconButton
              size="small"
              onClick={() => onRetry(attachmentId)}
              disabled={isRetryLoading}
              sx={{
                width: 20,
                height: 20,
                ml: 0.5,
                color: "error.main",
                "&:hover": { backgroundColor: "error.light", color: "error.dark" },
              }}
            >
              {isRetryLoading ? (
                <CircularProgress size={12} />
              ) : (
                <RefreshIcon sx={{ fontSize: 12 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

const renderEmbeddingStatusChip = (
  datasourceId: string,
  embeddingStatus: EmbeddingStatusResponse | null,
  retryLoadingMap: Record<string, boolean>,
  onRetry: (datasourceId: string) => void,
  datasource?: DataSource
) => {
  const status = getDatasourceEmbeddingStatus(datasourceId, embeddingStatus, datasource);
  // webscrapping_UI branch: also fetch the matched entry for error_message (failure tooltip,
  // e.g. the e-commerce block message) and scraped_pages (completed website count).
  const entry = getDatasourceEmbeddingEntry(datasourceId, embeddingStatus, datasource);

  // When status is null (e.g. not in embedding API yet), show empty so we don't flash "N/A" before "In progress"
  if (status === null) {
    return (
      <Chip
        label=""
        color="default"
        size="small"
        sx={{ fontSize: "0.75rem", height: "20px", ml: 1 }}
      />
    );
  }

  let label: string;
  let color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";

  switch (status) {
    case 1:
      label = "In Progress";
      color = "warning";
      break;
    case 2:
      label = entry?.scraped_pages != null ? `Completed · ${entry.scraped_pages} pages` : "Completed";
      color = "success";
      break;
    case 3:
      label = "Failure";
      color = "error";
      break;
    case 4:
      // Website-only transient state: crawl finished, embedding starts automatically right
      // after (is_embedding_required: true on create) — no manual step, so this flips to
      // "In Progress" (1) on its own almost immediately.
      label = entry?.scraped_pages != null ? `Scraping completed · ${entry.scraped_pages} pages` : "Scraping completed";
      color = "info";
      break;
    default:
      label = "Unknown";
      color = "default";
      break;
  }

  const isRetryLoading = retryLoadingMap[datasourceId] || false;

  const statusChip = (
    <Chip
      label={label}
      color={color}
      size="small"
      sx={{
        fontSize: "0.75rem",
        height: "20px",
        ml: 1
      }}
    />
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {status === 3 && entry?.error_message ? (
        <Tooltip title={entry.error_message}>
          <span>{statusChip}</span>
        </Tooltip>
      ) : (
        statusChip
      )}
      {status === 3 && (
        <Tooltip title="Retry embedding">
          <span>
            <IconButton
              size="small"
              onClick={() => onRetry(datasourceId)}
              disabled={isRetryLoading}
              sx={{
                width: 20,
                height: 20,
                ml: 0.5,
                color: "error.main",
                "&:hover": {
                  backgroundColor: "error.light",
                  color: "error.dark"
                }
              }}
            >
              {isRetryLoading ? (
                <CircularProgress size={12} />
              ) : (
                <RefreshIcon sx={{ fontSize: 12 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
      )}
      {/* No "Start Embedding" button at status 4 anymore — website creation now sets
          is_embedding_required: true, so the backend embeds automatically right after
          scraping (status moves 4 -> 1 -> 2/3 on its own) without a manual click. The
          regenerateEmbedding/onRetry call itself is untouched (status 3 retry below still
          uses it), so any pre-existing website datasource left stuck at status 4 from before
          this change can still be recovered by retrying it once it errors out, or via a
          backend-side sweep — there is just no dedicated button for it in this UI anymore. */}
      {/* webscrapping_fix branch: the eye icon that used to live here for website datasources
          is removed — clicking the datasource's name now opens the same scraped-content
          preview directly (handleDatasourceNameClick), matching the PDF flow (click name ->
          preview) instead of needing a separate icon next to the status chip. */}
    </Box>
  );
};

const DataSourcesPanel: React.FC<DataSourcesPanelProps> = ({
  channelId,
  channelName,
  isOpen,
  onClose,
  onOpenChange,
  threadId,
  messages = [],
  initialOpenAddDataSource = false,
  onInitialAddDataSourceOpened,
  onDataSourceAdded: onDataSourceAddedFromParent,
}) => {
  const { isDarkMode } = useTheme();
  const muiTheme = useMuiTheme();
  const { markSessionExpired } = useAuth();
  const { showSuccess, showError, showWarning } = useToast();

  // Check if current user is channel admin (convert null to undefined for hook)
  const { isChannelAdmin } = useChannelAdmin(channelId ?? undefined);

  // Tab state - always default to datasources when opening via Data Sources icon
  const [activeTab, setActiveTab] = useState<"datasources" | "attachments">(
    "datasources"
  );

  // Reset to datasources tab when panel opens; also clear failure banner so
  // a fresh fetch re-evaluates failures each time the panel is opened.
  useEffect(() => {
    if (isOpen) {
      setActiveTab("datasources");
      setFailedBannerDismissed(false);
      hasShownFailureToastRef.current = false;
    }
  }, [isOpen]);

  // State for datasources
  const [datasources, setDatasources] = useState<DataSource[]>([]);
  const [datasourcesLoading, setDatasourcesLoading] = useState<boolean>(false);
  const [datasourcesError, setDatasourcesError] = useState<string | null>(null);
  const [datasourcesPage, setDatasourcesPage] = useState<number>(1);
  const [datasourcesTotal, setDatasourcesTotal] = useState<number>(0);
  /** True while fetching additional pages (scroll pagination); keeps main list visible */
  const [datasourcesLoadingMore, setDatasourcesLoadingMore] =
    useState<boolean>(false);
  /** From last list response; used to gate load-more */
  const [datasourcesHasMore, setDatasourcesHasMore] = useState<boolean>(false);
  const [selectedDatasources, setSelectedDatasources] = useState<Set<string>>(
    new Set()
  );
  const [isDisconnectingDatasources, setIsDisconnectingDatasources] =
    useState<boolean>(false);
  const [disconnectModalOpen, setDisconnectModalOpen] =
    useState<boolean>(false);
  const [isReconnectingDatasources, setIsReconnectingDatasources] =
    useState<boolean>(false);
  const [reconnectModalOpen, setReconnectModalOpen] = useState<boolean>(false);

  // State for embedding status
  const [embeddingStatus, setEmbeddingStatus] = useState<EmbeddingStatusResponse | null>(null);
  const [embeddingStatusLoading, setEmbeddingStatusLoading] = useState<boolean>(false);
  const [embeddingStatusError, setEmbeddingStatusError] = useState<string | null>(null);
  const [retryLoadingMap, setRetryLoadingMap] = useState<Record<string, boolean>>({});
  // Controls visibility of the inline failure banner; reset every time the panel opens
  const [failedBannerDismissed, setFailedBannerDismissed] = useState(false);
  // Ensures the failure toast fires only once per panel-open, not on every embeddingStatus update
  const hasShownFailureToastRef = useRef(false);

  // State for AddLogDataSourceModal
  const [isAddDataSourceModalOpen, setIsAddDataSourceModalOpen] = useState(false);
  const initialAddDataSourceOpenedRef = useRef(false);

  // A plugin deep link needs to open both the panel and its child dialog. Consume the
  // request once so closing and reopening the panel manually does not unexpectedly reopen
  // the dialog.
  useEffect(() => {
    if (!initialOpenAddDataSource) {
      initialAddDataSourceOpenedRef.current = false;
      return;
    }
    if (!isOpen || initialAddDataSourceOpenedRef.current) return;

    initialAddDataSourceOpenedRef.current = true;
    setIsAddDataSourceModalOpen(true);
    onInitialAddDataSourceOpened?.();
  }, [initialOpenAddDataSource, isOpen, onInitialAddDataSourceOpened]);

  // Skill-linked datasource IDs — excluded from the channel panel display
  const [skillDatasourceIds, setSkillDatasourceIds] = useState<Set<string>>(new Set());

  // Pending datasource filenames (upload succeeded, create-bulk still in progress) — show as "In progress" in list
  const [pendingDatasourceFilenames, setPendingDatasourceFilenames] = useState<string[]>([]);

  // State for datasource view modal (supported files) and download (unsupported)
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalDatasourceId, setViewModalDatasourceId] = useState<
    string | null
  >(null);
  const [viewModalDisplayName, setViewModalDisplayName] = useState<string>("");
  const [viewModalDownloadSource, setViewModalDownloadSource] = useState<
    DownloadSourceParams | null
  >(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState<string | null>(
    null
  );

  // State for the website scrape-preview dialog (raw scraped text, GET .../download?view=true) —
  // separate from viewModalOpen/DatasourceViewModal above, which is blob+iframe/object based and
  // MIME-allowlist gated (deliberately, per a past security review) — not a fit for raw text.
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDialogTitle, setPreviewDialogTitle] = useState<string>("");
  const [previewDialogLoading, setPreviewDialogLoading] = useState(false);
  const [previewDialogError, setPreviewDialogError] = useState<string>("");
  const [previewDialogText, setPreviewDialogText] = useState<string>("");
  // webscrapping_fix branch: which datasource the preview dialog above is currently showing, so
  // its "Download file" action (added below, matching DatasourceViewModal's PDF pattern) knows
  // what to fetch — the eye icon only opens the preview text, it never downloaded before.
  const [previewDialogDatasourceId, setPreviewDialogDatasourceId] = useState<string>("");
  const [previewDownloadLoading, setPreviewDownloadLoading] = useState(false);

  const handlePreviewScrapedContent = async (datasourceId: string, displayName: string) => {
    setPreviewDialogOpen(true);
    setPreviewDialogTitle(displayName);
    setPreviewDialogDatasourceId(datasourceId);
    setPreviewDialogLoading(true);
    setPreviewDialogError("");
    setPreviewDialogText("");
    try {
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.DOWNLOAD(datasourceId)}?view=true`,
        {
          headers: {
            accept: "text/plain",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      setPreviewDialogText(await response.text());
    } catch (err) {
      setPreviewDialogError(err instanceof Error ? err.message : "Failed to load preview.");
    } finally {
      setPreviewDialogLoading(false);
    }
  };

  const handlePreviewDialogClose = () => {
    setPreviewDialogOpen(false);
    setPreviewDialogText("");
    setPreviewDialogError("");
    setPreviewDialogDatasourceId("");
  };

  // webscrapping_fix branch: "Download file" action for the scraped-content preview dialog,
  // mirroring DatasourceViewModal's PDF "Download file" link — the eye icon shows the scraped
  // text (handlePreviewScrapedContent above), this separately downloads it as a file. Website
  // datasources have no cloud downloadSource (gcp/azure/s3), so downloadDatasource is called
  // without one, same as any other non-cloud datasource.
  const handleDownloadScrapedContent = async () => {
    if (!previewDialogDatasourceId) return;
    setPreviewDownloadLoading(true);
    try {
      await downloadDatasource(previewDialogDatasourceId, previewDialogTitle || `datasource-${previewDialogDatasourceId}`);
      showSuccess("Download started");
    } catch (err) {
      showError(err instanceof Error ? err.message : "Failed to download datasource");
    } finally {
      setPreviewDownloadLoading(false);
    }
  };

  // State for attachments (datasources for the thread)
  const [threadAttachments, setThreadAttachments] = useState<DataSource[]>([]);
  const [threadAttachmentsLoading, setThreadAttachmentsLoading] = useState<boolean>(false);
  const [threadAttachmentsError, setThreadAttachmentsError] = useState<string | null>(null);
  const [selectedMessageAttachments, setSelectedMessageAttachments] = useState<Set<string>>(
    new Set()
  );

  // Scroll container for datasources tab infinite scroll (same pattern as ThreadList)
  const datasourcesScrollRef = useRef<HTMLDivElement | null>(null);
  const datasourcesScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const datasourcesLoadMoreLockRef = useRef<boolean>(false);

  // Fetch datasources; silent = no full-panel spinner; append = merge next page at bottom
  const fetchDatasources = async (
    page: number = 1,
    size: number = DATASOURCE_PAGE_SIZE,
    threadIdParam?: string | null,
    options?: { silent?: boolean; append?: boolean }
  ) => {
    const silent = options?.silent ?? false;
    const append = options?.append ?? false;

    if (!channelId) {
      return;
    }

    try {
      if (append) {
        setDatasourcesLoadingMore(true);
      } else if (!silent) {
        setDatasourcesLoading(true);
        setDatasourcesError(null);
      }

      const params = new URLSearchParams({
        page: page.toString(),
        size: size.toString(),
        channel_id: channelId,
      });

      // Add thread_id if provided
      if (threadIdParam) {
        params.append('thread_id', threadIdParam);
      }

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: DataSourcesResponse = await response.json();

      setDatasourcesTotal(data.total);
      setDatasourcesPage(data.page);
      setDatasourcesHasMore(computeDatasourcesHasMore(data));

      if (append) {
        setDatasources((prev) => {
          const existingIds = new Set(prev.map((d) => d.id));
          const merged = [...prev];
          for (const ds of data.datasources) {
            if (!existingIds.has(ds.id)) merged.push(ds);
          }
          return merged;
        });
        setPendingDatasourceFilenames((prev) =>
          prev.filter(
            (pendingName) =>
              !data.datasources.some(
                (ds) =>
                  sameDatasourceName(ds.filename, pendingName) ||
                  sameDatasourceName(ds.name, pendingName)
              )
          )
        );
      } else {
        setDatasources(data.datasources);
        setPendingDatasourceFilenames((prev) =>
          prev.filter(
            (pendingName) =>
              !data.datasources.some(
                (ds) =>
                  sameDatasourceName(ds.filename, pendingName) ||
                  sameDatasourceName(ds.name, pendingName)
              )
          )
        );
      }
    } catch (err) {
      let errorMessage = `Failed to fetch ${LanguageConstants.datasource_s.toLowerCase()}`;

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              errorMessage = "Authentication failed. Please log in again.";
              break;
            case "403":
              errorMessage =
                "You do not have permission to access datasources.";
              break;
            case "404":
              errorMessage = "Datasources not found.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load ${LanguageConstants.datasource_s.toLowerCase()} (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage =
            "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      if (append) {
        showError(errorMessage);
      } else {
        setDatasourcesError(errorMessage);
        if (!silent) showError(errorMessage);
      }
    } finally {
      if (append) {
        setDatasourcesLoadingMore(false);
      } else if (!silent) {
        setDatasourcesLoading(false);
      }
    }
  };

  // Load next page when user scrolls to bottom (channel list only; no thread_id)
  const loadMoreDatasources = useCallback(() => {
    if (
      !channelId ||
      datasourcesLoadingMore ||
      !datasourcesHasMore ||
      datasourcesLoading
    ) {
      return;
    }
    void fetchDatasources(datasourcesPage + 1, DATASOURCE_PAGE_SIZE, undefined, {
      append: true,
    });
  }, [
    channelId,
    datasourcesPage,
    datasourcesLoadingMore,
    datasourcesHasMore,
    datasourcesLoading,
  ]);

  // Near-bottom scroll detection (aligned with ThreadList threshold + debounce)
  const handleDatasourcesScroll = useCallback(() => {
    if (activeTab !== "datasources") return;
    const container = datasourcesScrollRef.current;
    if (
      !container ||
      datasourcesLoadingMore ||
      !datasourcesHasMore ||
      datasourcesLoading ||
      datasourcesLoadMoreLockRef.current
    ) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = container;
    const thresholdPx = 100;
    if (scrollHeight - scrollTop - clientHeight < thresholdPx) {
      if (datasourcesScrollTimeoutRef.current) {
        clearTimeout(datasourcesScrollTimeoutRef.current);
      }
      datasourcesScrollTimeoutRef.current = setTimeout(() => {
        if (
          !datasourcesLoadMoreLockRef.current &&
          !datasourcesLoadingMore &&
          datasourcesHasMore &&
          !datasourcesLoading
        ) {
          datasourcesLoadMoreLockRef.current = true;
          loadMoreDatasources();
        }
      }, 150);
    }
  }, [
    activeTab,
    datasourcesLoadingMore,
    datasourcesHasMore,
    datasourcesLoading,
    loadMoreDatasources,
  ]);

  useEffect(() => {
    const container = datasourcesScrollRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleDatasourcesScroll);
    return () => container.removeEventListener("scroll", handleDatasourcesScroll);
  }, [handleDatasourcesScroll]);

  useEffect(() => {
    if (!datasourcesLoadingMore) {
      datasourcesLoadMoreLockRef.current = false;
      if (!datasourcesHasMore || activeTab !== "datasources") return;
      const timeoutId = setTimeout(() => {
        const container = datasourcesScrollRef.current;
        if (
          container &&
          container.scrollHeight <= container.clientHeight &&
          !datasourcesLoading &&
          !datasourcesLoadingMore
        ) {
          loadMoreDatasources();
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [
    datasourcesLoadingMore,
    datasourcesHasMore,
    loadMoreDatasources,
    activeTab,
    datasourcesLoading,
  ]);

  useEffect(() => {
    if (
      !datasourcesLoading &&
      !datasourcesLoadingMore &&
      datasourcesHasMore &&
      datasources.length > 0 &&
      activeTab === "datasources"
    ) {
      const container = datasourcesScrollRef.current;
      if (container && container.scrollHeight <= container.clientHeight) {
        loadMoreDatasources();
      }
    }
  }, [
    datasourcesLoading,
    datasourcesLoadingMore,
    datasourcesHasMore,
    datasources.length,
    loadMoreDatasources,
    activeTab,
  ]);

  useEffect(() => {
    return () => {
      if (datasourcesScrollTimeoutRef.current) {
        clearTimeout(datasourcesScrollTimeoutRef.current);
      }
    };
  }, []);

  // Fetch attachments for a thread (calls both APIs)
  const fetchThreadAttachments = useCallback(async () => {
    if (!channelId || !threadId) {
      return;
    }

    try {
      setThreadAttachmentsLoading(true);
      setThreadAttachmentsError(null);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";

      // First API: Fetch datasources with thread_id
      const params = new URLSearchParams({
        page: "1",
        size: "10",
        channel_id: channelId,
        thread_id: threadId,
      });

      const datasourcesResponse = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!datasourcesResponse.ok) {
        throw new Error(`HTTP error! status: ${datasourcesResponse.status}`);
      }

      const datasourcesData: DataSourcesResponse = await datasourcesResponse.json();

      // Second API: Fetch embedding status (with thread_id for Attachments tab)
      const embeddingParams = new URLSearchParams({ thread_id: threadId });
      const embeddingResponse = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.EMBEDDING_STATUS}${channelId}?${embeddingParams}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!embeddingResponse.ok) {
        throw new Error(`HTTP error! status: ${embeddingResponse.status}`);
      }

      const embeddingData: EmbeddingStatusResponse = await embeddingResponse.json();

      // Set the attachments (datasources) and embedding status
      setThreadAttachments(datasourcesData.datasources);
      setEmbeddingStatus(embeddingData);
    } catch (err) {
      let errorMessage = "Failed to fetch thread attachments";

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage = "You do not have permission to access attachments.";
              break;
            case "404":
              errorMessage = "Attachments not found.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load attachments (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage = "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      setThreadAttachmentsError(errorMessage);
      showError(errorMessage);
    } finally {
      setThreadAttachmentsLoading(false);
    }
  }, [channelId, threadId, markSessionExpired, showError]);

  // Fetch embedding status for a channel; when silent is true, no loading spinner (e.g. after 2nd API)
  const fetchEmbeddingStatus = async (silent?: boolean) => {
    if (!channelId) {
      return;
    }

    try {
      if (!silent) {
        setEmbeddingStatusLoading(true);
        setEmbeddingStatusError(null);
      }

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.EMBEDDING_STATUS}${channelId}`,
        {
          method: "GET",
          headers: {
            accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: EmbeddingStatusResponse = await response.json();
      setEmbeddingStatus(data);
    } catch (err) {
      let errorMessage = "Failed to fetch embedding status";

      if (err instanceof Error) {
        if (err.message.includes("HTTP error! status:")) {
          const statusMatch = err.message.match(/status: (\d+)/);
          const statusCode = statusMatch ? statusMatch[1] : "";

          switch (statusCode) {
            case "401":
              markSessionExpired();
              return;
            case "403":
              errorMessage = "You do not have permission to access embedding status.";
              break;
            case "404":
              errorMessage = "Embedding status not found.";
              break;
            case "500":
              errorMessage = "Server error. Please try again later.";
              break;
            default:
              errorMessage = `Failed to load embedding status (Error ${statusCode}). Please try again.`;
          }
        } else if (err.message.includes("fetch")) {
          errorMessage = "Network error. Please check your connection and try again.";
        } else {
          errorMessage = err.message;
        }
      }

      setEmbeddingStatusError(errorMessage);
      if (!silent) console.error("Error fetching embedding status:", err);
    } finally {
      if (!silent) setEmbeddingStatusLoading(false);
    }
  };

  // Standalone "N data source(s) failed to process" warning toast — hidden (commented out,
  // not removed). The inline banner inside the Data Sources panel (below) still shows this
  // same failure info and is unaffected; only this separate floating popup is disabled.
  // hasShownFailureToastRef is still reset elsewhere on panel close/reopen — that reset is
  // now a harmless no-op since nothing reads the ref while this effect is disabled.
  //
  // useEffect(() => {
  //   if (!embeddingStatus || embeddingStatus.failed === 0) return;
  //   if (hasShownFailureToastRef.current) return;
  //   hasShownFailureToastRef.current = true;
  //   const count = embeddingStatus.failed;
  //   showWarning(
  //     `${count} data source${count > 1 ? 's' : ''} failed to process. Open the Data Sources panel to retry.`
  //   );
  // }, [embeddingStatus, showWarning]);

  // Regenerate embedding for a specific datasource
  const regenerateEmbedding = async (datasourceId: string) => {
    try {
      setRetryLoadingMap(prev => ({ ...prev, [datasourceId]: true }));

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";
      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.REGENERATE_EMBEDDING(datasourceId)}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setEmbeddingStatus(prevStatus => {
          if (!prevStatus) return prevStatus;
          
          return {
            ...prevStatus,
            datasources: prevStatus.datasources.map(ds => 
              ds.id === datasourceId 
                ? { ...ds, embedding_status: 1 } // Set to in progress
                : ds
            )
          };
        });
        
        // Fetch updated embedding status after a short delay
        setTimeout(() => {
          fetchEmbeddingStatus();
          // If attachments tab is active, refresh attachments too
          if (activeTab === "attachments" && threadId) {
            fetchThreadAttachments();
          }
        }, 1000);
      }
    } catch (error) {
      console.error("Error regenerating embedding:", error);
      showError("Failed to regenerate embedding");
    } finally {
      setRetryLoadingMap(prev => ({ ...prev, [datasourceId]: false }));
    }
  };

  // Function to handle select all datasources (for connected section) — uses deduped list so it matches displayed rows
  const handleSelectAllConnectedDatasources = (checked: boolean) => {
    const connected = datasources.filter((ds) => ds.is_connected);
    const deduped = connected.filter(
      (ds, i, arr) =>
        arr.findIndex(
          (d) =>
            sameDatasourceName(d.filename, ds.filename) ||
            sameDatasourceName(d.name, ds.filename)
        ) === i
    );
    const newSelected = new Set(selectedDatasources);
    if (checked) {
      deduped.forEach((ds) => newSelected.add(ds.id));
    } else {
      deduped.forEach((ds) => newSelected.delete(ds.id));
    }
    setSelectedDatasources(newSelected);
  };

  // Function to handle select all datasources (for disconnected section)
  const handleSelectAllDisconnectedDatasources = (checked: boolean) => {
    const disconnectedDatasources = datasources.filter(
      (ds) => !ds.is_connected
    );
    const newSelected = new Set(selectedDatasources);

    if (checked) {
      disconnectedDatasources.forEach((ds) => newSelected.add(ds.id));
    } else {
      disconnectedDatasources.forEach((ds) => newSelected.delete(ds.id));
    }
    setSelectedDatasources(newSelected);
  };

  // Function to handle select all attachments (for connected section in Attachments tab)
  const handleSelectAllConnectedAttachments = (checked: boolean) => {
    const connected = threadAttachments.filter((ds) => ds.is_connected);
    const newSelected = new Set(selectedDatasources);
    if (checked) {
      connected.forEach((ds) => newSelected.add(ds.id));
    } else {
      connected.forEach((ds) => newSelected.delete(ds.id));
    }
    setSelectedDatasources(newSelected);
  };

  // Function to handle select all attachments (for disconnected section in Attachments tab)
  const handleSelectAllDisconnectedAttachments = (checked: boolean) => {
    const disconnected = threadAttachments.filter((ds) => !ds.is_connected);
    const newSelected = new Set(selectedDatasources);
    if (checked) {
      disconnected.forEach((ds) => newSelected.add(ds.id));
    } else {
      disconnected.forEach((ds) => newSelected.delete(ds.id));
    }
    setSelectedDatasources(newSelected);
  };

  // Function to handle message attachment selection
  const handleMessageAttachmentSelection = (
    attachmentId: string,
    checked: boolean
  ) => {
    const newSelected = new Set(selectedMessageAttachments);
    if (checked) {
      newSelected.add(attachmentId);
    } else {
      newSelected.delete(attachmentId);
    }
    setSelectedMessageAttachments(newSelected);
  };

  // Function to handle message attachment toggle
  const handleMessageAttachmentToggle = (attachmentId: string) => {
    // Message attachments are always considered "connected" since they're in the "Currently querying from" section
    // When toggled, we'll handle it similar to datasource toggles
    // Find the attachment from messages
    const messageAttachmentsList: ThreadAttachment[] = [];
    messages.forEach((message) => {
      if (message.attachments && message.attachments.length > 0) {
        message.attachments.forEach((attachment) => {
          if (!attachment.is_inline) {
            messageAttachmentsList.push(attachment);
          }
        });
      }
    });
    
    const attachment = messageAttachmentsList.find((att) => att.attachment_id === attachmentId);
    if (attachment) {
      // Set this attachment as selected and open disconnect modal
      // Since message attachments are always "connected", toggling off should disconnect them
      setSelectedMessageAttachments(new Set([attachmentId]));
      // For message attachments, we might need a different API endpoint
      // For now, we'll use the same disconnect flow but you may need to adapt this
      setDisconnectModalOpen(true);
    }
  };

  // Function to handle individual datasource selection
  const handleDatasourceSelection = (
    datasourceId: string,
    checked: boolean
  ) => {
    const newSelected = new Set(selectedDatasources);
    if (checked) {
      newSelected.add(datasourceId);
    } else {
      newSelected.delete(datasourceId);
    }
    setSelectedDatasources(newSelected);
  };

  // Handle click on datasource name: preview in modal (supported) or download (unsupported)
  const handleDatasourceNameClick = async (datasource: DataSource) => {
    if (datasource.id.startsWith("pending-")) return; // Pending uploads are not viewable/downloadable yet
    const displayName = getDataSourceDisplayName(datasource);
    // webscrapping_fix branch: website datasources now follow the exact same click-to-preview
    // flow as PDFs — clicking the name opens a preview (this dialog, for scraped text;
    // DatasourceViewModal below, for PDFs/images), never a direct download. Checked first,
    // ahead of isDatasourceSupportedForPreview, since that check is MIME-allowlist based
    // (DatasourceViewModal's blob+iframe rendering) and was never meant to cover raw scraped
    // text — this used to be reachable only via a separate eye icon, now removed.
    const isWebsite = datasource.storage_type === 'website' || Boolean(datasource.url);
    if (isWebsite) {
      await handlePreviewScrapedContent(datasource.id, datasource.name || datasource.filename || displayName || 'Scraped content');
      return;
    }
    if (isDatasourceSupportedForPreview(datasource)) {
      setViewModalDatasourceId(datasource.id);
      setViewModalDisplayName(displayName);
      setViewModalDownloadSource(getDownloadSourceParams(datasource.provider));
      setViewModalOpen(true);
    } else {
      setDownloadLoadingId(datasource.id);
      try {
        await downloadDatasource(
          datasource.id,
          datasource.filename || datasource.name || displayName || `datasource-${datasource.id}`,
          getDownloadSourceParams(datasource.provider)
        );
        showSuccess("Download started");
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : "Failed to download datasource";
        showError(msg);
      } finally {
        setDownloadLoadingId(null);
      }
    }
  };

  // Function to handle disconnect button click - opens confirmation modal
  const handleDisconnectButtonClick = () => {
    if (selectedDatasources.size === 0 && selectedMessageAttachments.size === 0) {
      showWarning("Please select datasources or attachments to disconnect");
      return;
    }
    setDisconnectModalOpen(true);
  };

  // Function to handle toggle switch click - opens appropriate confirmation modal
  const handleToggleSwitchClick = (datasourceId: string) => {
    setSelectedDatasources(new Set([datasourceId]));

    const datasource = datasources.find((ds) => ds.id === datasourceId);
    if (datasource) {
      if (datasource.is_connected) {
        setDisconnectModalOpen(true);
      } else {
        setReconnectModalOpen(true);
      }
    }
  };

  // Function to handle toggle switch click in Attachments tab - same as handleToggleSwitchClick but uses threadAttachments
  const handleAttachmentToggleClick = (datasourceId: string) => {
    setSelectedDatasources(new Set([datasourceId]));
    const datasource = threadAttachments.find((ds) => ds.id === datasourceId);
    if (datasource) {
      if (datasource.is_connected) {
        setDisconnectModalOpen(true);
      } else {
        setReconnectModalOpen(true);
      }
    }
  };

  // Resolve message attachment IDs to datasource IDs (match by id or filename)
  const resolveMessageAttachmentsToDatasourceIds = (
    attachmentIds: string[],
    messagesList: Array<{ attachments: ThreadAttachment[] | null }>
  ): string[] => {
    const ids: string[] = [];
    for (const attachmentId of attachmentIds) {
      const att = messagesList
        .flatMap((m) => m.attachments ?? [])
        .find((a) => !a.is_inline && a.attachment_id === attachmentId);
      if (att) {
        const match =
          threadAttachments.find((ds) => ds.id === attachmentId) ??
          threadAttachments.find(
            (ds) =>
              ds.filename === att.filename ||
              getDataSourceDisplayName(ds) === att.filename
          );
        ids.push(match?.id ?? attachmentId);
      }
    }
    return ids;
  };

  // Function to handle actual disconnect operation
  const handleConfirmDisconnect = async () => {
    try {
      setIsDisconnectingDatasources(true);

      const datasourceIds = [
        ...new Set([
          ...Array.from(selectedDatasources),
          ...resolveMessageAttachmentsToDatasourceIds(
            Array.from(selectedMessageAttachments),
            messages
          ),
        ]),
      ].filter(Boolean);

      if (datasourceIds.length === 0) {
        showWarning("No datasources selected to disconnect");
        return;
      }

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.BULK_CONNECTION}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            datasource_ids: datasourceIds,
            action: "disconnect",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      showSuccess("Datasources disconnected successfully");
      setSelectedDatasources(new Set());
      setSelectedMessageAttachments(new Set());
      setDisconnectModalOpen(false);

      const disconnectedIds = new Set(datasourceIds);
      setDatasources((prev) =>
        prev.map((ds) =>
          disconnectedIds.has(ds.id) ? { ...ds, is_connected: false } : ds
        )
      );
      setThreadAttachments((prev) =>
        prev.map((ds) =>
          disconnectedIds.has(ds.id) ? { ...ds, is_connected: false } : ds
        )
      );
      void fetchEmbeddingStatus(true);
      if (activeTab === "attachments" && threadId) {
        fetchThreadAttachments();
      }
    } catch (error) {
      showError("Failed to disconnect datasources");
    } finally {
      setIsDisconnectingDatasources(false);
    }
  };

  // Function to handle modal close
  const handleDisconnectModalClose = () => {
    setDisconnectModalOpen(false);
  };

  // Function to handle re-connect button click - opens confirmation modal
  const handleReconnectButtonClick = () => {
    if (selectedDatasources.size === 0) {
      showWarning("Please select datasources to reconnect");
      return;
    }
    setReconnectModalOpen(true);
  };

  // Function to handle actual reconnect operation
  const handleConfirmReconnect = async () => {
    try {
      setIsReconnectingDatasources(true);

      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || "";

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.BULK_CONNECTION}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            datasource_ids: Array.from(selectedDatasources),
            action: "connect",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      showSuccess("Datasources reconnected successfully");
      const reconnectedIds = new Set(Array.from(selectedDatasources));
      setSelectedDatasources(new Set());
      setReconnectModalOpen(false);

      setDatasources((prev) =>
        prev.map((ds) =>
          reconnectedIds.has(ds.id) ? { ...ds, is_connected: true } : ds
        )
      );
      setThreadAttachments((prev) =>
        prev.map((ds) =>
          reconnectedIds.has(ds.id) ? { ...ds, is_connected: true } : ds
        )
      );
      void fetchEmbeddingStatus(true);
      if (activeTab === "attachments" && threadId) {
        fetchThreadAttachments();
      }
    } catch (error) {
      showError("Failed to reconnect datasources");
    } finally {
      setIsReconnectingDatasources(false);
    }
  };

  // Function to handle reconnect modal close
  const handleReconnectModalClose = () => {
    setReconnectModalOpen(false);
  };

  const handleAddDataSourceClick = () => {
    setIsAddDataSourceModalOpen(true);
  };

  const handleAddDataSourceClose = () => {
    setIsAddDataSourceModalOpen(false);
    // Same as Admin Hub: refetch after modal close so list stays in sync (single source of truth, no duplicate rows)
    if (channelId) {
      setTimeout(() => {
        fetchDatasources(1, DATASOURCE_PAGE_SIZE, undefined, {
          silent: true,
        });
        fetchEmbeddingStatus(true);
      }, 200);
    }
  };

  // Check if all connected datasources are selected
  const connectedDatasources = datasources.filter((ds) => ds.is_connected);
  const disconnectedDatasources = datasources.filter((ds) => !ds.is_connected);

  // Dedupe connected list by logical filename so we never show two real rows for the same file
  const connectedDatasourcesDeduped = connectedDatasources.filter(
    (ds, i, arr) =>
      arr.findIndex(
        (d) =>
          sameDatasourceName(d.filename, ds.filename) ||
          sameDatasourceName(d.name, ds.filename)
      ) === i
  );

  // Exclude skill-linked datasources from the channel panel.
  // Checks two sources:
  //   1. skillDatasourceIds — populated async from fetchSkills (covers saved skills).
  //   2. getSkillLinkedDatasourceIds() — module-level registry written immediately by SkillsTab
  //      after upload, so files are hidden even before the skill is saved to the backend.
  const _registryIds = getSkillLinkedDatasourceIds();
  const displayConnectedDatasources = connectedDatasourcesDeduped.filter(
    (ds) => !skillDatasourceIds.has(ds.id) && !_registryIds.has(ds.id)
  );

  const isAllConnectedSelected =
    connectedDatasourcesDeduped.length > 0 &&
    connectedDatasourcesDeduped.every((ds) => selectedDatasources.has(ds.id));
  const isConnectedIndeterminate =
    connectedDatasourcesDeduped.some((ds) => selectedDatasources.has(ds.id)) &&
    !connectedDatasourcesDeduped.every((ds) => selectedDatasources.has(ds.id));

  const isAllDisconnectedSelected =
    disconnectedDatasources.length > 0 &&
    disconnectedDatasources.every((ds) => selectedDatasources.has(ds.id));
  const isDisconnectedIndeterminate =
    disconnectedDatasources.some((ds) => selectedDatasources.has(ds.id)) &&
    !disconnectedDatasources.every((ds) => selectedDatasources.has(ds.id));

  // Counts for bulk Disconnect / Re-connect (shown at top and bottom of each section)
  const selectedConnectedBulkCount = Array.from(selectedDatasources).filter(
    (id) => datasources.find((ds) => ds.id === id)?.is_connected
  ).length;
  const showConnectedBulkDisconnect =
    !datasourcesLoading &&
    !datasourcesError &&
    datasources.length > 0 &&
    displayConnectedDatasources.length > 0 &&
    selectedConnectedBulkCount > 0 &&
    isChannelAdmin;

  const selectedDisconnectedBulkCount = Array.from(selectedDatasources).filter(
    (id) => datasources.find((ds) => ds.id === id)?.is_connected === false
  ).length;
  const showDisconnectedBulkReconnect =
    disconnectedDatasources.length > 0 &&
    selectedDisconnectedBulkCount > 0;

  // Fetch datasources when panel is opened
  useEffect(() => {
    if (isOpen && channelId) {
      if (activeTab === "attachments" && threadId) {
        // Fetch attachments when attachments tab is active
        fetchThreadAttachments();
      } else if (activeTab === "datasources") {
        // Fetch datasources when datasources tab is active
        fetchDatasources(1, DATASOURCE_PAGE_SIZE);
        fetchEmbeddingStatus();
        // Fetch skill-linked datasource IDs to exclude them from this panel.
        // Best-effort / eventually consistent: re-fetched on every panel open or tab switch.
        // SkillsTab force-refreshes the cache after a successful link so the next open is up-to-date.
        fetchSkills(channelId).then((skills) => {
          const ids = new Set(
            skills.flatMap((s) => s.knowledge_files?.map((kf) => kf.datasource_id) ?? [])
          );
          setSkillDatasourceIds(ids);
        }).catch(() => {});
      }
    }
    if (onOpenChange) {
      onOpenChange(isOpen);
    }
  }, [isOpen, channelId, activeTab, threadId, fetchThreadAttachments]);

  // Poll while a website datasource is still processing — embedding_status 4 ("Scraping
  // completed", crawl done, embedding about to start automatically) or 1 ("In Progress",
  // embedding actually running) — as a fallback alongside the shay_realtime socket listener
  // below, in case a status-update event is ever missed (e.g. socket briefly disconnected).
  // Covering status 4 here too (not just 1) guards the specific window between crawl finishing
  // and embedding starting: that transition is expected to be near-instant since embedding is
  // now automatic, but if it ever has real latency (e.g. an embedding-job queue backlog), a
  // missed socket event would otherwise leave the row stuck on "Scraping completed" until the
  // panel is closed and reopened. Reads embeddingStatus via a ref (kept in sync below) so the
  // interval itself is created once per panel-open, not recreated on every fetch, and only
  // actually calls the API while a website row is genuinely still processing — it's a no-op
  // the rest of the time.
  const embeddingStatusRef = useRef<EmbeddingStatusResponse | null>(null);
  useEffect(() => {
    embeddingStatusRef.current = embeddingStatus;
  }, [embeddingStatus]);

  useEffect(() => {
    if (!isOpen || !channelId || activeTab !== "datasources") return;
    const interval = setInterval(() => {
      const stillProcessing = embeddingStatusRef.current?.datasources?.some(
        (ds) => (ds.embedding_status === 1 || ds.embedding_status === 4) && ds.url
      );
      if (stillProcessing) {
        fetchEmbeddingStatus(true);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isOpen, channelId, activeTab]);

  // Real-time website-scrape status (webscrapping_UI branch) — shay_realtime type 16
  // (EMBEDDING_STATUS_UPDATED) events, keyed by datasourceId, so status flips (4 scraping ->
  // 1 in progress -> 2/3 completed/failed) show up live instead of waiting on the 5s poll
  // above. The poll is left in place as a fallback in case a socket event is missed — this
  // listener only ever updates a datasource that's already loaded in local state; if the id
  // isn't found yet (e.g. the very first event right after creation), it falls back to a
  // silent refetch instead of dropping the event. Registers/unregisters with its own callback
  // reference (never a bare socketService.off("shay_realtime")) so it doesn't clobber other
  // pages' listeners on the same shared "shay_realtime" channel (e.g. ThreadDetailPage's).
  useEffect(() => {
    if (!isOpen || !channelId || activeTab !== "datasources") return;

    const handleEmbeddingStatusUpdate = (eventData: { type: number; data: any }) => {
      if (eventData?.type !== SOCKET_KEYS.EMBEDDING_STATUS_UPDATED) return;
      const payload = eventData.data || {};
      const datasourceId = payload.datasourceId ?? payload.datasource_id ?? payload.id;
      const status = payload.status ?? payload.embedding_status;
      if (!datasourceId || status == null) return;

      const exists = embeddingStatusRef.current?.datasources?.some((ds) => ds.id === datasourceId);
      if (!exists) {
        fetchEmbeddingStatus(true);
        return;
      }

      setEmbeddingStatus((prev) => {
        if (!prev?.datasources) return prev;
        return {
          ...prev,
          datasources: prev.datasources.map((ds) =>
            ds.id === datasourceId
              ? {
                  ...ds,
                  embedding_status: status,
                  ...(payload.scraped_pages != null ? { scraped_pages: payload.scraped_pages } : {}),
                  ...(payload.error_message != null ? { error_message: payload.error_message } : {}),
                }
              : ds
          ),
        };
      });
    };

    socketService.on("shay_realtime", handleEmbeddingStatusUpdate);
    return () => socketService.off("shay_realtime", handleEmbeddingStatusUpdate);
  }, [isOpen, channelId, activeTab]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Source Panel - Overlay: above subscription cancellation banner (drawer + 2) so header is not behind it */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "400px",
          height: "100vh",
          backgroundColor: muiTheme.palette.background.paper,
          borderLeft: `1px solid ${muiTheme.palette.divider}`,
          // Channel Dashboard / Settings are full-screen Drawers pinned at zIndex 1300 and are now the
          // permanent base layer for a channel, so this panel must sit above that (its own Dialogs below
          // are bumped to 1400 to stay above the panel itself).
          zIndex: DATA_SOURCES_PANEL_Z_INDEX,
          boxShadow: isDarkMode
            ? "-2px 0 8px rgba(0,0,0,0.3)"
            : "-2px 0 8px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Panel Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${muiTheme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {activeTab === "datasources" ? LanguageConstants.datasource_s : "Attachments"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {activeTab === "datasources" && isChannelAdmin && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddDataSourceClick}
                sx={{
                  backgroundColor: "primary.main",
                  "&:hover": { backgroundColor: "primary.dark" },
                }}
              >
                {LanguageConstants.datasource}
              </Button>
            )}
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: `1px solid ${muiTheme.palette.divider}` }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{
              minHeight: "48px",
              "& .MuiTab-root": {
                minHeight: "48px",
                textTransform: "none",
                fontSize: "0.875rem",
                fontWeight: 500,
              },
            }}
          >
            <Tab
              label="Datasources"
              value="datasources"
              sx={{
                color: activeTab === "datasources" ? "primary.main" : "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                },
              }}
            />
            <Tab
              label="Attachments"
              value="attachments"
              sx={{
                color: activeTab === "attachments" ? "primary.main" : "text.secondary",
                "&.Mui-selected": {
                  color: "primary.main",
                },
              }}
            />
          </Tabs>
        </Box>

        {/* Panel Content — scroll container for datasource infinite scroll */}
        <Box ref={datasourcesScrollRef} sx={{ flex: 1, overflow: "auto", p: 1 }}>
          {activeTab === "attachments" ? (
            // Attachments Tab Content - same structure as Datasources tab
            (() => {
              const connectedDatasources = threadAttachments.filter((ds) => ds.is_connected);
              const disconnectedDatasources = threadAttachments.filter((ds) => !ds.is_connected);
              const hasConnectedItems = connectedDatasources.length > 0;

              const isAllConnectedSelected =
                connectedDatasources.length > 0 &&
                connectedDatasources.every((ds) => selectedDatasources.has(ds.id));
              const isConnectedIndeterminate =
                connectedDatasources.some((ds) => selectedDatasources.has(ds.id)) &&
                !connectedDatasources.every((ds) => selectedDatasources.has(ds.id));
              const isAllDisconnectedSelected =
                disconnectedDatasources.length > 0 &&
                disconnectedDatasources.every((ds) => selectedDatasources.has(ds.id));
              const isDisconnectedIndeterminate =
                disconnectedDatasources.some((ds) => selectedDatasources.has(ds.id)) &&
                !disconnectedDatasources.every((ds) => selectedDatasources.has(ds.id));

              return (
            <>
              {threadAttachmentsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : threadAttachmentsError ? (
                <Box sx={{ p: 2 }}>
                  <Alert severity="error" sx={{ fontSize: "0.875rem" }}>{threadAttachmentsError}</Alert>
                </Box>
              ) : !threadId ? (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">Please select a thread to view attachments</Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", mb: 2, px: 1 }}>
                    Currently querying from
                  </Typography>

                  {hasConnectedItems ? (
                    <TableContainer sx={{ mb: 2 }}>
                      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40, p: 0, pl: 1 }}>
                              <Checkbox
                                size="small"
                                indeterminate={isConnectedIndeterminate}
                                checked={isAllConnectedSelected}
                                onChange={(e) =>
                                  handleSelectAllConnectedAttachments(e.target.checked)
                                }
                                disabled={connectedDatasources.length === 0 || !isChannelAdmin}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "50%" }}>File Name</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "25%", textAlign: "center" }}>Embedding</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "25%", textAlign: "right" }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {connectedDatasources.map((datasource) => (
                            <TableRow key={datasource.id}>
                              <TableCell sx={{ p: 0, pl: 1 }}>
                                <Checkbox
                                  size="small"
                                  checked={selectedDatasources.has(datasource.id)}
                                  onChange={(e) =>
                                    handleDatasourceSelection(
                                      datasource.id,
                                      e.target.checked
                                    )
                                  }
                                  disabled={!isChannelAdmin}
                                />
                              </TableCell>
                              <TableCell sx={{ p: 1 }}>
                                <Box
                                  onClick={() =>
                                    handleDatasourceNameClick(datasource)
                                  }
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor:
                                      downloadLoadingId === datasource.id
                                        ? "wait"
                                        : "pointer",
                                    "&:hover": { opacity: 0.8 },
                                  }}
                                  title={
                                    isDatasourceSupportedForPreview(datasource)
                                      ? "Click to view"
                                      : "Click to download"
                                  }
                                >
                                  {getDataSourceIcon(datasource)}
                                  {downloadLoadingId === datasource.id ? (
                                    <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                  ) : null}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.875rem",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={getDataSourceDisplayName(datasource)}
                                  >
                                    {getDataSourceDisplayName(datasource)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ p: 1, textAlign: "center" }}>
                                {renderEmbeddingStatusChip(datasource.id, embeddingStatus, retryLoadingMap, regenerateEmbedding, datasource)
                                  ?? <Chip label="" color="default" size="small" sx={{ fontSize: "0.75rem", height: "20px" }} />
                                }
                              </TableCell>
                              <TableCell sx={{ p: 1, textAlign: "right" }}>
                                <Switch
                                  size="small"
                                  checked={datasource.is_connected}
                                  onChange={() =>
                                    handleAttachmentToggleClick(datasource.id)
                                  }
                                  disabled={!isChannelAdmin}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">No connected attachments</Typography>
                    </Box>
                  )}

                  <Typography variant="body2" sx={{ fontWeight: "bold", color: "text.secondary", mb: 2, px: 1 }}>
                    Disconnected data sources
                  </Typography>

                  {disconnectedDatasources.length > 0 ? (
                    <TableContainer>
                      <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40, p: 0, pl: 1 }}>
                              <Checkbox
                                size="small"
                                indeterminate={isDisconnectedIndeterminate}
                                checked={isAllDisconnectedSelected}
                                onChange={(e) =>
                                  handleSelectAllDisconnectedAttachments(
                                    e.target.checked
                                  )
                                }
                                disabled={disconnectedDatasources.length === 0 || !isChannelAdmin}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "50%" }}>File Name</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "25%", textAlign: "center" }}>Embedding</TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: "0.875rem", width: "25%", textAlign: "right" }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {disconnectedDatasources.map((datasource) => (
                            <TableRow key={datasource.id}>
                              <TableCell sx={{ p: 0, pl: 1 }}>
                                <Checkbox
                                  size="small"
                                  checked={selectedDatasources.has(datasource.id)}
                                  onChange={(e) =>
                                    handleDatasourceSelection(
                                      datasource.id,
                                      e.target.checked
                                    )
                                  }
                                />
                              </TableCell>
                              <TableCell sx={{ p: 1 }}>
                                <Box
                                  onClick={() =>
                                    handleDatasourceNameClick(datasource)
                                  }
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor:
                                      downloadLoadingId === datasource.id
                                        ? "wait"
                                        : "pointer",
                                    "&:hover": { opacity: 0.8 },
                                  }}
                                  title={
                                    isDatasourceSupportedForPreview(datasource)
                                      ? "Click to view"
                                      : "Click to download"
                                  }
                                >
                                  {getDataSourceIcon(datasource)}
                                  {downloadLoadingId === datasource.id ? (
                                    <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                  ) : null}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.875rem",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={getDataSourceDisplayName(datasource)}
                                  >
                                    {getDataSourceDisplayName(datasource)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ p: 1, textAlign: "center" }}>
                                {renderEmbeddingStatusChip(datasource.id, embeddingStatus, retryLoadingMap, regenerateEmbedding, datasource)
                                  ?? <Chip label="" color="default" size="small" sx={{ fontSize: "0.75rem", height: "20px" }} />
                                }
                              </TableCell>
                              <TableCell sx={{ p: 1, textAlign: "right" }}>
                                <Switch
                                  size="small"
                                  checked={datasource.is_connected}
                                  onChange={() =>
                                    handleAttachmentToggleClick(datasource.id)
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="body2" color="text.secondary">No disconnected datasources</Typography>
                    </Box>
                  )}
                </>
              )}
            </>
              );
            })()
          ) : (
            // Datasources Tab Content
            <>
              {/* Failure alert banner — shown whenever any datasource has embedding_status === 3 (Failure).
                  Dismissible per panel-open session; reappears next time the panel is opened if
                  failures still exist, so the user is never silently left with a broken pipeline. */}
              {!failedBannerDismissed &&
                embeddingStatus &&
                embeddingStatus.failed > 0 && (
                  <Alert
                    severity="error"
                    onClose={() => setFailedBannerDismissed(true)}
                    sx={{ mx: 1, mb: 2, fontSize: "0.8rem", alignItems: "flex-start" }}
                  >
                    <strong>
                      {embeddingStatus.failed} data source
                      {embeddingStatus.failed > 1 ? "s" : ""} failed to process.
                    </strong>{" "}
                    {embeddingStatus.datasources
                      .filter((ds) => ds.embedding_status === 3)
                      .map((ds) => ds.filename || ds.name)
                      .join(", ")}
                    . Use the{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>
                      ↺
                    </Box>{" "}
                    retry button next to each file to reprocess.
                  </Alert>
                )}

              {/* Section title and bulk Disconnect on one row (Datasources tab) */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 2,
                  px: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", color: "text.secondary" }}
                >
                  Currently querying from
                </Typography>
                {showConnectedBulkDisconnect ? (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={handleDisconnectButtonClick}
                    disabled={isDisconnectingDatasources}
                    startIcon={
                      isDisconnectingDatasources ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <LinkOffIcon />
                      )
                    }
                    sx={{
                      backgroundColor: "#d32f2f",
                      "&:hover": { backgroundColor: "#b71c1c" },
                      "&:disabled": { backgroundColor: "#ffcdd2" },
                    }}
                  >
                    {isDisconnectingDatasources
                      ? "Disconnecting..."
                      : "Disconnect"}
                  </Button>
                ) : null}
              </Box>

          {datasourcesLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : datasourcesError ? (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" sx={{ fontSize: "0.875rem" }}>
                {datasourcesError}
              </Alert>
            </Box>
          ) : datasources.length === 0 ? (
            <Box sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No {LanguageConstants.datasource_s.toLowerCase()} found
              </Typography>
            </Box>
          ) : (
            <>
              {/* Connected Datasources (includes pending uploads with "In progress" embedding) */}
              {(() => {
                return displayConnectedDatasources.length > 0 ? (
                  <>
                    <TableContainer
                      sx={{ mb: showConnectedBulkDisconnect ? 0 : 2 }}
                    >
                      <Table
                        size="small"
                        sx={{ tableLayout: "fixed", width: "100%" }}
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 40, p: 0, pl: 1 }}>
                              <Checkbox
                                size="small"
                                indeterminate={isConnectedIndeterminate}
                                checked={isAllConnectedSelected}
                                onChange={(e) =>
                                  handleSelectAllConnectedDatasources(
                                    e.target.checked
                                  )
                                }
                                disabled={!isChannelAdmin}
                              />
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                width: "50%",
                              }}
                            >
                              File Name
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                width: "25%",
                                textAlign: "center",
                              }}
                            >
                              Embedding
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.875rem",
                                width: "25%",
                                textAlign: "right",
                              }}
                            >
                              Status
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {displayConnectedDatasources.map((datasource) => {
                            const isPending = datasource.id.startsWith("pending-");
                            return (
                            <TableRow key={datasource.id}>
                              <TableCell sx={{ p: 0, pl: 1 }}>
                                <Checkbox
                                  size="small"
                                  checked={selectedDatasources.has(
                                    datasource.id
                                  )}
                                  onChange={(e) =>
                                    handleDatasourceSelection(
                                      datasource.id,
                                      e.target.checked
                                    )
                                  }
                                  disabled={!isChannelAdmin || isPending}
                                />
                              </TableCell>
                              <TableCell sx={{ p: 1 }}>
                                <Box
                                  onClick={() =>
                                    handleDatasourceNameClick(datasource)
                                  }
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    cursor:
                                      downloadLoadingId === datasource.id
                                        ? "wait"
                                        : "pointer",
                                    "&:hover": {
                                      opacity: 0.8,
                                    },
                                  }}
                                  title={
                                    isDatasourceSupportedForPreview(datasource)
                                      ? "Click to view"
                                      : "Click to download"
                                  }
                                >
                                  {getDataSourceIcon(datasource)}
                                  {downloadLoadingId === datasource.id ? (
                                    <CircularProgress size={14} sx={{ mr: 0.5 }} />
                                  ) : null}
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: "0.875rem",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                    title={getDataSourceDisplayName(
                                      datasource
                                    )}
                                  >
                                    {getDataSourceDisplayName(datasource)}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ p: 1, textAlign: "center" }}>
                                {renderEmbeddingStatusChip(datasource.id, embeddingStatus, retryLoadingMap, regenerateEmbedding, datasource)}
                              </TableCell>
                              <TableCell sx={{ p: 1, textAlign: "right" }}>
                                <Switch
                                  size="small"
                                  checked={datasource.is_connected}
                                  onChange={() =>
                                    handleToggleSwitchClick(datasource.id)
                                  }
                                  disabled={!isChannelAdmin || isPending}
                                />
                              </TableCell>
                            </TableRow>
                          ); })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No connected datasources
                    </Typography>
                  </Box>
                );
              })()}
            </>
          )}

          {/* Disconnected section: title + optional bulk Re-connect on one row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
              mb: 2,
              px: 1,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: "bold", color: "text.secondary" }}
            >
              Disconnected {LanguageConstants.datasource_s.toLowerCase()}
            </Typography>
            {showDisconnectedBulkReconnect ? (
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleReconnectButtonClick}
                disabled={isReconnectingDatasources}
                startIcon={
                  isReconnectingDatasources ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <LinkIcon />
                  )
                }
                sx={{
                  backgroundColor: "#1976d2",
                  "&:hover": { backgroundColor: "#1565c0" },
                  "&:disabled": { backgroundColor: "#bbdefb" },
                }}
              >
                {isReconnectingDatasources
                  ? "Reconnecting..."
                  : "Re-connect"}
              </Button>
            ) : null}
          </Box>

          {disconnectedDatasources.length > 0 ? (
              <>
                <TableContainer
                  sx={{ mb: showDisconnectedBulkReconnect ? 0 : 2 }}
                >
                  <Table
                    size="small"
                    sx={{ tableLayout: "fixed", width: "100%" }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 40, p: 0, pl: 1 }}>
                          <Checkbox
                            size="small"
                            indeterminate={isDisconnectedIndeterminate}
                            checked={isAllDisconnectedSelected}
                            onChange={(e) =>
                              handleSelectAllDisconnectedDatasources(
                                e.target.checked
                              )
                            }
                            disabled={!isChannelAdmin}
                          />
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            width: "50%",
                          }}
                        >
                          File Name
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            width: "25%",
                            textAlign: "center",
                          }}
                        >
                          Embedding
                        </TableCell>
                        <TableCell
                          sx={{
                            fontWeight: "bold",
                            fontSize: "0.875rem",
                            width: "25%",
                            textAlign: "right",
                          }}
                        >
                          Status
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {disconnectedDatasources.map((datasource) => (
                        <TableRow key={datasource.id}>
                          <TableCell sx={{ p: 0, pl: 1 }}>
                            <Checkbox
                              size="small"
                              checked={selectedDatasources.has(datasource.id)}
                              onChange={(e) =>
                                handleDatasourceSelection(
                                  datasource.id,
                                  e.target.checked
                                )
                              }
                              disabled={!isChannelAdmin}
                            />
                          </TableCell>
                          <TableCell sx={{ p: 1 }}>
                            <Box
                              onClick={() =>
                                handleDatasourceNameClick(datasource)
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                cursor:
                                  downloadLoadingId === datasource.id
                                    ? "wait"
                                    : "pointer",
                                "&:hover": {
                                  opacity: 0.8,
                                },
                              }}
                              title={
                                isDatasourceSupportedForPreview(datasource)
                                  ? "Click to view"
                                  : "Click to download"
                              }
                            >
                              {getDataSourceIcon(datasource)}
                              {downloadLoadingId === datasource.id ? (
                                <CircularProgress size={14} sx={{ mr: 0.5 }} />
                              ) : null}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.875rem",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                                title={getDataSourceDisplayName(datasource)}
                              >
                                {getDataSourceDisplayName(datasource)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ p: 1, textAlign: "center" }}>
                            {renderEmbeddingStatusChip(datasource.id, embeddingStatus, retryLoadingMap, regenerateEmbedding, datasource)}
                          </TableCell>
                          <TableCell sx={{ p: 1, textAlign: "right" }}>
                            <Switch
                              size="small"
                              checked={datasource.is_connected}
                              onChange={() =>
                                handleToggleSwitchClick(datasource.id)
                              }
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
          ) : (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No disconnected datasources
                </Typography>
              </Box>
          )}

          {datasourcesLoadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                py: 2,
              }}
            >
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading more…
              </Typography>
            </Box>
          )}
            </>
          )}
        </Box>
      </Box>

      {/* Backdrop when source panel is open: below the panel (1350) but above Dashboard/Settings (1300), which are the permanent base layer for a channel now */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: isDarkMode
            ? "rgba(0, 0, 0, 0.4)"
            : "rgba(0, 0, 0, 0.15)",
          zIndex: DATA_SOURCES_BACKDROP_Z_INDEX,
          pointerEvents: "auto",
        }}
        onClick={onClose}
      />

      {/* Add Log DataSource Modal */}
      <AddLogDataSourceModal
        data-testid="add-datasource-modal"
        open={isAddDataSourceModalOpen}
        onClose={handleAddDataSourceClose}
        onDataSourceAdded={() => {
          // Same as Admin Hub: only show what the API returns (no pending/optimistic rows) to avoid duplication
          fetchDatasources(1, DATASOURCE_PAGE_SIZE, undefined, {
            silent: true,
          });
          fetchEmbeddingStatus(true);
          onDataSourceAddedFromParent?.();
        }}
        channelId={channelId ?? undefined}
      />

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        data-testid="disconnect-datasource-dialog"
        open={disconnectModalOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          handleDisconnectModalClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        disableEnforceFocus
        disableAutoFocus
        sx={{ zIndex: DATA_SOURCES_DIALOG_Z_INDEX }}
      >
        <DialogTitle>Disconnect Datasources</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to disconnect the selected datasources?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will disconnect{" "}
              <strong>
                (
                {(() => {
                  const selectedDatasourceList = Array.from(selectedDatasources);
                  const selectedAttachmentList = Array.from(selectedMessageAttachments);
                  const totalCount = selectedDatasourceList.length + selectedAttachmentList.length;

                  let displayName: string;
                  if (selectedDatasourceList.length > 0) {
                    const firstDatasource =
                      datasources.find((ds) => ds.id === selectedDatasourceList[0]) ??
                      threadAttachments.find((ds) => ds.id === selectedDatasourceList[0]);
                    displayName = firstDatasource
                      ? getDataSourceDisplayName(firstDatasource)
                      : "";
                  } else if (selectedAttachmentList.length > 0) {
                    displayName = getMessageAttachmentDisplayName(
                      selectedAttachmentList[0],
                      messages
                    );
                  } else {
                    displayName = "";
                  }
                  const additionalCount = totalCount - 1;

                  return (
                    <>
                      {displayName}
                      {additionalCount > 0 && ` +${additionalCount}`}
                    </>
                  );
                })()}
                )
              </strong>{" "}
              from the <strong>{channelName || "channel"}</strong>. You
              can reconnect them later if needed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDisconnectModalClose}
            disabled={isDisconnectingDatasources}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDisconnect}
            variant="contained"
            color="error"
            disabled={isDisconnectingDatasources}
          >
            {isDisconnectingDatasources ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Disconnecting...
              </>
            ) : (
              "Disconnect"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-connect Confirmation Dialog */}
      <Dialog
        data-testid="reconnect-datasource-dialog"
        open={reconnectModalOpen}
        onClose={(event, reason) => {
          if (reason === "backdropClick") {
            return;
          }
          handleReconnectModalClose();
        }}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown
        disableEnforceFocus
        disableAutoFocus
        sx={{ zIndex: DATA_SOURCES_DIALOG_Z_INDEX }}
      >
        <DialogTitle>Reconnect Datasources</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body1">
              Are you sure you want to reconnect the selected datasources?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              This will reconnect{" "}
              <strong>
                (
                {(() => {
                  const selectedDatasourceList =
                    Array.from(selectedDatasources);
                  const firstDatasource =
                    datasources.find((ds) => ds.id === selectedDatasourceList[0]) ??
                    threadAttachments.find((ds) => ds.id === selectedDatasourceList[0]);
                  const displayName = firstDatasource
                    ? getDataSourceDisplayName(firstDatasource)
                    : "";
                  const additionalCount = selectedDatasources.size - 1;

                  return (
                    <>
                      {displayName}
                      {additionalCount > 0 && ` +${additionalCount}`}
                    </>
                  );
                })()}
                )
              </strong>{" "}
              to the <strong>{channelName || "channel"}</strong>. Once
              reconnected, these datasources will be available for querying.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleReconnectModalClose}
            disabled={isReconnectingDatasources}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmReconnect}
            variant="contained"
            color="primary"
            disabled={isReconnectingDatasources}
          >
            {isReconnectingDatasources ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />
                Reconnecting...
              </>
            ) : (
              "Reconnect"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Datasource view modal for PDF, images, txt */}
      <DatasourceViewModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewModalDatasourceId(null);
          setViewModalDisplayName("");
          setViewModalDownloadSource(null);
        }}
        datasourceId={viewModalDatasourceId || ""}
        displayName={viewModalDisplayName}
        downloadSource={viewModalDownloadSource}
      />

      {/* Scraped-website content preview — raw text from GET .../download?view=true, shown
          once embedding_status is 4 (scraping completed) or 2 (embedded). Separate from
          DatasourceViewModal above since that's blob+iframe/object based and MIME-allowlist
          gated (deliberately, per a past security review) — not a fit for raw scraped text.
          webscrapping_fix branch: header/actions restructured to match DatasourceViewModal's
          own PDF layout exactly — title + X close in one row, a right-aligned "Download file"
          link above the content, no bottom action bar — so website datasources now present the
          identical click-to-preview flow as PDFs, not a differently-shaped dialog. */}
      <Dialog
        open={previewDialogOpen}
        onClose={handlePreviewDialogClose}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: DATA_SOURCES_DIALOG_Z_INDEX }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontWeight: "bold" }}>
            {previewDialogTitle}
          </Typography>
          <IconButton
            onClick={handlePreviewDialogClose}
            sx={{ color: "text.secondary" }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0, minHeight: 400 }}>
          {previewDialogLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
              <CircularProgress />
            </Box>
          ) : previewDialogError ? (
            <Alert severity="error" sx={{ mt: 2 }}>{previewDialogError}</Alert>
          ) : (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                <Typography variant="body2">
                  <Box
                    component="span"
                    onClick={previewDownloadLoading ? undefined : handleDownloadScrapedContent}
                    sx={{
                      cursor: previewDownloadLoading ? "default" : "pointer",
                      color: previewDownloadLoading ? "text.disabled" : "primary.main",
                      textDecoration: previewDownloadLoading ? "none" : "underline",
                    }}
                  >
                    {previewDownloadLoading ? "Downloading..." : "Download file"}
                  </Box>
                </Typography>
              </Box>
              <Typography
                component="pre"
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "monospace",
                  maxHeight: "60vh",
                  overflowY: "auto",
                  m: 0,
                }}
              >
                {previewDialogText || "No content available."}
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DataSourcesPanel;
